import { Component, Input, ViewChild, ElementRef, AfterViewInit, OnDestroy, Output, EventEmitter, OnInit, HostListener } from '@angular/core';
import { NgForm } from "@angular/forms";
import { ScoutTaskService, ScoutPointPhotoService, UploadProgressSubject, UploadPhotoState, ScoutPointPhotoUploadService, TransferPhotoProgressSubject, TransferPhotoProgress, PhotoChangeEvent, RemotePhoto, UploadCompleteEvent, TransferPhotoCompleteEvent } from '../../../core/services';
import { ScoutPoint, ScoutPointPhoto } from '../../../core/models';
import { ScoutTaskDoc } from '../../../core/schema/scout-task-schema';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AppState } from '../../../core';
import { getType as getMimeType } from 'mime'
import { ScoutPointPhotoDoc } from '../../../core/schema/scout-point-photo-schema';
import { BehaviorSubject, Subject, Subscription, timer } from 'rxjs';
import { FesBaseComponent } from '../../../shared/components/fes-base.component';
import {Md5} from 'ts-md5/dist/md5';
import { MediaUtils } from '../../../core/media-utils'
import { WebcamImage, WebcamInitError } from 'ngx-webcam';
import { filter, take, takeWhile } from 'rxjs/operators';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { v4 as uuidv4 } from 'uuid'
import { RxReplicationState } from 'rxdb';
import { ReplicationStateListener } from '../../../core/services/testbed-rxdb';

@Component({
  selector: 'app-scout-point-photo-editor',
  templateUrl: './scout-point-photo-editor.component.html',
  styleUrls: ['./scout-point-photo-editor.component.scss']
})
export class ScoutPointPhotoEditorComponent extends FesBaseComponent implements OnInit, AfterViewInit, OnDestroy, ReplicationStateListener {

  //static defaultCamera : MediaDeviceInfo

  @Input() scoutTaskDoc : ScoutTaskDoc
  @Input() scoutPathId : string
  @Input() scoutPoint : ScoutPoint
  @Input() isEditing : boolean
  @Input() locked : boolean
  @Input() editableTitle = false
  @Input() editableDescription = false
  @Input() groupName : string
  @Input() deleteEnabled = true
  @Input() moveEnabled = true
  @Input() readOnly = false

  @ViewChild('photoForm') photoForm: NgForm;

  @Input() photoClickHandler = ( photo : ScoutPointPhoto, url : string ) => null;

  @Input() set scoutPhoto(photoDoc : ScoutPointPhotoDoc) {
    //console.log('set scoutPhoto(' + photoDoc?._id + ')')
    this.photoDoc = photoDoc
    this.reset()
    if ( photoDoc ) {
      const uploadProgressEmitter = this.photoUploadService.uploadProgressSubjects.find(e=>e.photoId==photoDoc._id)
      if ( uploadProgressEmitter ) {
        // upload is already in progress so listen for percent complete
        this.addUploadProgressSubscription(uploadProgressEmitter)
      }
      const downloadProgressEmitter = this.photoService.downloadProgressSubjects.find(e=>e.photoId==photoDoc._id)
      if ( downloadProgressEmitter ) {
        // download is already in progress so listen for percent complete
        this.addDownloadProgressSubscription(downloadProgressEmitter)
      }
    }
  }

  @Input() selectedPhoto = new BehaviorSubject<ScoutPointPhoto>(null)

  @Output('editChange') editChange = new EventEmitter<NgForm>()
  @Output('deleted') deletedEmitter = new EventEmitter<ScoutPointPhotoDoc>()
  @Output('saved') savedEmitter = new EventEmitter<ScoutPointPhotoDoc>()
  @Output('orderChanged') orderChangedEmitter = new EventEmitter<ScoutPointPhotoDoc>()

  @ViewChild('outerDiv') outerDivRef: ElementRef;
  @ViewChild('alterPhotoCanvas') alterPhotoCanvas: ElementRef;

  readonly MISSING_PHOTO_URL = '/assets/background.jpg'

  photo: ScoutPointPhoto
  photoDoc: ScoutPointPhotoDoc
  errorMsg : string
  downloadProgress: TransferPhotoProgress;
  uploadState: UploadPhotoState;
  photoFile : File
  photoUrl : string // base64 encoded file data as url
  urlPath : string
  fileName : string // just a placeholder for form dirty tracking
  bProcessing : boolean
  mobileOs: string
  webcamTrigger = new Subject<void>();
  webcamImageType: string = 'image/jpeg'
  bShowWebcamView = false
  webcamViewHeight = 300
  webcamViewWidth = 320
  webcamVideoOptions : MediaTrackConstraints
  uploadProgressSubscription : Subscription
  downloadProgressSubscription : Subscription
  maximizeCamView = true

  bAlteringPhoto = false
  bIncludeTextInMove = true
  bSaving = false

  constructor(
      appState : AppState,
      protected scoutTaskService : ScoutTaskService,
      public photoService : ScoutPointPhotoService,
      protected photoUploadService : ScoutPointPhotoUploadService,
      protected bsModalService : BsModalService,
      public elementRef: ElementRef
  ) {
    super(appState)
    this.urlPath = `${appState.settings.getApiUrl()}/scout/ref-file`
  }


  @HostListener('window:resize', ['$event'])
  onResize(event?: Event) {
    if ( this.maximizeCamView ) {
      const win = !!event ? (event.target as Window) : window;
      this.webcamViewWidth = win.innerWidth;
      this.webcamViewHeight = win.innerHeight;
      console.log(`width: ${this.webcamViewWidth}, height: ${this.webcamViewHeight}`)
    }
  }

  onMaximizeCamViewModeChange(evt:any) {
    this.appState.settings.setMaximizeWebcamViewEnabled(this.maximizeCamView)
  }

  ngOnInit() {
    this.maximizeCamView = this.appState.settings.getMaximizeWebcamViewEnabled()
    this.selectedCameraId = this.appState.settings.getDefaultWebcamId()
    this.mobileOs = MediaUtils.getMobileOs()

    //Find uploads for this photo from photo service and listen to upload percent complete for progress bar
    this.subscribe(this.photoUploadService.uploadStartedEmitter, (progressSubject:UploadProgressSubject)=>{
      if ( progressSubject.photoId == this.photoDoc?._id ) {
        this.addUploadProgressSubscription(progressSubject)
      }
    })
    this.subscribe(this.photoService.downloadStartEmitter, (progressSubject:TransferPhotoProgressSubject)=>{
      if ( progressSubject.photoId == this.photoDoc?._id ) {
        this.addDownloadProgressSubscription(progressSubject)
      }
    })
    this.subscribe(this.photoService.downloadCompleteEmitter, ( evt : TransferPhotoCompleteEvent ) => {
      if ( evt.remotePhoto?._id == this.photo?._id) {
        //console.log( 'Download complete: ' + evt.remotePhoto?._id )
        //if ( evt.remotePhoto?._rev != photoDoc._rev ) {
        //  console.warn('Downloaded photo from sync does not match current version')
        //}
        this.photoUrl = evt.dataUrl
        this.errorMsg = evt.error ? this.photoService.extractErrorMsg(evt.error) : null
      }
    })
    this.subscribe(this.photoUploadService.uploadCompleteEmitter, ( evt : UploadCompleteEvent) => {
      if ( evt.photoDoc?._id == this.photo?._id) {
        //console.log( 'Upload complete: ' + evt.photoDoc?._id )
        this.photo.uploadDate = evt.photoDoc.uploadDate
        this.errorMsg = evt.error ? this.photoService.extractErrorMsg(evt.error) : null
      }
    })
    this.photoService.syncChangeListeners.push(this)
  }

  ngAfterViewInit(): void {
    this.subscribe(this.photoForm.statusChanges, status => this.editChange.emit(this.photoForm))
    this.subscribe(this.photoForm.valueChanges, change => this.editChange.emit(this.photoForm))
  }


  ngOnDestroy(): void {
    this.uploadProgressSubscription?.unsubscribe()
    this.downloadProgressSubscription?.unsubscribe()
    this.photoService.syncChangeListeners = this.photoService.syncChangeListeners.filter(l=>l!=this)
    this.destroy()
  }


  // listen for external sych changes
  replicationStateCreated = (replicationState: RxReplicationState) => {
    replicationState.change$.subscribe( (changeEvent : PhotoChangeEvent) => {
      if ( changeEvent?.direction == 'pull' && changeEvent.change?.docs) {
        let docs : RemotePhoto[] = changeEvent.change.docs.filter(d=>d._id==this.photoDoc._id)
        if ( docs.length ) {
          const doc = docs[docs.length-1]
          if ( doc._id == this.photoDoc._id ) {
            if ( doc._deleted ) {
              this.setFlashMsg('Remote delete occurred.')
              this.photoDoc.deleted$.pipe(take(1)).subscribe(d=>{
                this.deletedEmitter.emit(this.photoDoc)
              })
            } else {
              this.setFlashMsg('Remote update occurred.')
              if ( !doc._revisions || doc._revisions.length == 0 ) {
                // insert (should never get here because we already have an existing photo)
              } else {
                // modified
              }
              this.photoDoc.$.pipe(filter(d=>d._rev==doc._rev),take(1)).subscribe(async d=>{
                this.errorMsg = ''
                this.photo = d
                if ( !d.uploadDate ) {
                  this.photoUrl = null
                  this.photoFile = null
                  this.fileName = '';
                }
                console.log('doc.$='+JSON.stringify(d))
                //console.log('external='+JSON.stringify(doc))
                this.photoForm?.form?.markAsPristine()
              })
            }
          }
        }
      }
    })
  }

  flashMsg : string

  setFlashMsg(msg:string,seconds:number=3) {
    this.flashMsg = msg
    timer(seconds*1000).subscribe(_=>{
      if (msg==this.flashMsg) {
        this.flashMsg = ''
      }
    })
  }

  replicationStateCanceled(replicationState: RxReplicationState) {

  }


  addUploadProgressSubscription(progressSubject: UploadProgressSubject) {
    //console.log('addUploadProgressSubscription() photoId = ' + progressSubject?.photoId)
    this.uploadProgressSubscription?.unsubscribe()
    this.uploadState = progressSubject.value
    this.uploadProgressSubscription = progressSubject.subscribe(
      (uploadState: UploadPhotoState) => {
        this.uploadState = uploadState
        this.errorMsg = this.uploadState?.errorMsg
        //console.log('Upload progress: ' + this.uploadState.percentComplete)
      }
    )
  }

  addDownloadProgressSubscription(progressSubject: TransferPhotoProgressSubject) {
    //console.log('addDownloadProgressSubscription() photoId = ' + progressSubject?.photoId)
    this.downloadProgressSubscription?.unsubscribe()
    this.downloadProgress = progressSubject.value
    this.downloadProgressSubscription = progressSubject.subscribe(
      (progress: TransferPhotoProgress) => {
        //console.log('Download progress: ' + JSON.stringify(progress))
        this.downloadProgress = progress
        this.errorMsg = this.downloadProgress?.errorMsg
        if ( progress?.bComplete && !progress.errorMsg ) {

        }
      }
    )
  }


  async deletePhotoFile() {
    console.log('deletePhotoFile()')
    this.bProcessing = true
    this.bSaving = true

    try {
      if ( !this.appState.isOffline() && this.photo.uploadDate ) {
        try {
          await this.photoService.deleteRemotePhotoFile(this.photo).toPromise()
        } catch (error) {
          this.errorMsg = this.scoutTaskService.extractErrorMsg(error)
        }
      } else {
        // server will have to remove orphaned photo...
        console.warn(`Could not delete remote copy of photo file ${this.photo._id} (orphaned)`)
      }
      try {
        await this.photoService.removeLocalPhotoFile(this.photo._id)
      } catch ( error ) {
        console.error('Failed deleting rxdb scout file attachment')
        console.error(error)
      }
      this.photoUrl = null
      await this.photoDoc.atomicUpdate( (doc)=>{
        this.photoService.assignWhoAndWhen(doc)
        doc.fileName = null
        doc.fileChecksum = null
        doc.uploadDate = null
        return doc
      })
      this.photo.fileName = null
      this.photo.fileChecksum = null
      this.photo.uploadDate = null
    } catch ( error ) {
      console.error(error)
      this.errorMsg = this.scoutTaskService.extractErrorMsg(error)
    } finally {
      this.bSaving = false
      this.bProcessing = false
    }
  }


  isOnline = () => !this.appState.isOfflineSubject.value


  isReadonlyTitle = ( photo : ScoutPointPhoto ) => ['measurement','wide-angle'].includes(photo.group)


  async loadPhotoFile() {
    console.log('loadPhotoFile() name=' + this.photo.fileName + ', uploadDate=' + this.photo.uploadDate + ', id=' + this.photo?._id)

    this.bProcessing = true
    try {
      this.errorMsg = ''
      this.photoUrl = null

      try{
          this.photoUrl = await this.photoService.loadLocalPhotoFile(this.photo._id, this.photo.fileChecksum)
          if ( this.photoUrl ) {
            return
          }
      } catch ( error ) {
        console.error('Failed loading local photo file from rxdb.  Photo ID: ' + this.photo._id)
        console.error(error)
      }

      if ( this.appState.isOffline() ) {
        this.photoUrl = this.MISSING_PHOTO_URL
        this.errorMsg =  "Image source is offline."
      } else if ( !this.photo.uploadDate ) {
        this.photoUrl = this.MISSING_PHOTO_URL
        //console.log('this.downloadProgress=' + JSON.stringify(this.downloadProgress))
        this.errorMsg = 'Image is not uploaded and not found locally'
      } else {
        await this.loadPhotoFileFromServer()
      }
    } catch ( err ) {
      console.error(err)
      this.errorMsg =  this.scoutTaskService.extractErrorMsg(err)
    } finally {
      this.bProcessing = false
    }
  }


  async loadPhotoFileFromServer() {
    console.log('loadPhotoFileFromServer()')
    try{
      if ( !this.photo.mimeType ) {
        const mimeType = getMimeType(this.photo.fileName)
        this.photo.mimeType = mimeType ? mimeType as string : 'image/png'
        console.warn('Photo mimeType not set.  Using ' + this.photo.mimeType + ' from ' + this.photo.fileName)
      }
      const photo = this.photo
      const photoUrl = await this.photoService.downloadScoutRefPhotoFile(photo._id,photo.mimeType)
      // make sure editor is still showing same photo after long downloads
      if ( photo._id == this.photo._id ) {
        this.photoUrl = photoUrl
        if ( photo.userId == this.appState.getUser()?._id && !await this.photoService.isLocalPhotoFileCurrent(photo)){
          await this.photoService.saveLocalPhotoFile(photo, photoUrl)
        }
      }
    } catch ( err ) {
      this.errorMsg =  this.scoutTaskService.extractErrorMsg(err)
      console.error(this.errorMsg)
    } finally {
      this.downloadProgress = null
    }
  }


  async movePhoto(groupOrderIndexOffset: number = 0, bSwapPhotos: boolean = false ) {
    let selectedPhotoObj = this.selectedPhoto.value
    if ( !selectedPhotoObj._id ) {
      this.photoService.publishError('Source photo of move must be saved before moving', this)
      this.selectedPhoto.next(null)
      return
    }

    this.bProcessing = true
    this.bSaving = true

    let srcPhotoDoc = await this.photoService.rxCollection.findOne(selectedPhotoObj._id).exec()
    let srcObj = srcPhotoDoc.toJSON()
    let destPhotoDoc = this.photoDoc
    let destObj = this.photo
    const bSrcReadonlyTitle = this.isReadonlyTitle(srcObj)
    const bDestReadonlyTitle = this.isReadonlyTitle(destObj)

    this.selectedPhoto.next(null)

    try {
      // The source photo is reused/modified to be the new dest (so we dont need to copy photo image data)
      console.log('Before: ' + JSON.stringify(srcPhotoDoc.toJSON()))
      await srcPhotoDoc.atomicUpdate( doc => {
        doc.group = destObj.group
        doc.scoutPointId = this.scoutPoint.id
        doc.groupOrderIndex = destObj.groupOrderIndex + groupOrderIndexOffset // use a fraction to squeeze between existing photos
        if ( bDestReadonlyTitle || !this.bIncludeTextInMove ) {
          doc.title = destObj.title
        }
        if ( !this.bIncludeTextInMove ) {
          doc.notes = destObj.notes
        }
        return doc
      })
      this.photoDoc = srcPhotoDoc
      // update the "photo" from the source photo component to make sure it has right group, order, etc...
      selectedPhotoObj.group = srcPhotoDoc.group
      selectedPhotoObj.scoutPointId = srcPhotoDoc.scoutPointId
      selectedPhotoObj.groupOrderIndex = srcPhotoDoc.groupOrderIndex
      selectedPhotoObj.title = srcPhotoDoc.title
      selectedPhotoObj.notes = srcPhotoDoc.notes

      if ( bSwapPhotos ) {
        // swap action
        await destPhotoDoc.atomicUpdate( doc => {
          doc.scoutPointId = srcObj.scoutPointId
          doc.group = srcObj.group
          doc.groupOrderIndex = srcObj.groupOrderIndex
          if ( bSrcReadonlyTitle || !this.bIncludeTextInMove ) {
            doc.title = srcObj.title
          }
          if ( !this.bIncludeTextInMove ) {
            doc.notes = srcObj.notes
          }
          return doc
        })
      } else if ( destPhotoDoc._id && groupOrderIndexOffset == 0) {
        // replace action
        await destPhotoDoc.remove()
        this.deletedEmitter.emit(destPhotoDoc)
      } else {
        // insert before or after action
        this.orderChangedEmitter.emit(destPhotoDoc)
      }
      //this.reset()
      console.log('AfterAfter: ' + JSON.stringify(this.photo))

    } catch ( error ) {
      this.photoService.publishError(error,this)
    } finally {
      this.bIncludeTextInMove = true
      this.bProcessing = false
      this.bSaving = false
    }
  }


  onCancel() {
    this.errorMsg = '';
    this.bShowWebcamView = false;
    //TODO force camera to close
  }


  async onDelete() {
    console.log('onDelete() photo._id=' + this.photo._id)
    const deleteFn = async () => {
      try {
        this.bProcessing = true
        this.bSaving = true
        await this.photoService.beginPhotoTransaction(this.photo._id)
        this.uploadProgressSubscription?.unsubscribe()
        this.downloadProgressSubscription?.unsubscribe()
        if ( this.photo._id ) {
          await this.deletePhotoFile()
          await this.photoDoc.remove()
        }
        this.deletedEmitter.emit(this.photoDoc)
      } catch ( err ) {
        console.error('onDelete() failed')
        console.error(err)
        this.errorMsg =  this.scoutTaskService.extractErrorMsg(err)
      } finally {
        this.photoService.endPhotoTransaction(this.photo._id)
        this.bProcessing = false
        this.bSaving = false
      }
    }
    let initialState = {
      onYes: () => {
        deleteFn()
      },
      question: `Delete photo and metadata (cannot be undone)?`,
      focusOnNo: true,
      yesStyle: 'btn btn-danger'
    }
    this.bsModalService.show(ConfirmModalComponent, {initialState} );
  }


  async onFileInputChanged(event) {
    this.bProcessing = true
    try {
      this.errorMsg = ''
      const inputFile = (event.target as HTMLInputElement).files[0];
      const imageType = (inputFile?.type || '').toLowerCase()
      if ( !['image/jpg', 'image/png', 'image/jpeg' ].includes(imageType) ) {
        this.errorMsg = "Unsupported image format (type="+imageType+", file="+inputFile.name+")"
        return
      }
      this.photoFile = inputFile
      var reader = new FileReader();
      console.log('Converting photo in form data to self contained URL (base64)')
      const fileLoadPromise = new Promise<void>( (completeFn,errorFn) => {
        reader.onload = async (event) => {
          this.photoUrl = reader.result as string;
          completeFn()
        }
        reader.onerror = (error)  => {
          errorFn(error)
        }
      })
      await reader.readAsDataURL(this.photoFile);
      await fileLoadPromise
    } catch ( error ) {
      this.errorMsg = 'Loading local input file data.'
      console.error(error)
    } finally {
      this.bProcessing = false
    }
  }


  onMoveBefore() {
    this.movePhoto(-0.5)
  }


  onMoveAfter() {
    this.movePhoto(+0.5)
  }


  async onMoveSwap() {
    const bSwapPhotos = true
    this.movePhoto(0,bSwapPhotos)
  }


  async onMoveReplace() {
    this.movePhoto()
  }


  async onOverwrite() {
    // Force re-save from photo actions drop down of existing picture
    this.photoFile = await this.photoService.dataUrlToFileObject(this.photoUrl, this.photo.fileName, this.photo.mimeType )
    this.onSave()
  }


  async onPhotoClick() {
    if ( this.photoClickHandler ) {
      this.photoClickHandler(this.photo,this.photoUrl)
    }
  }


  async onReloadPhoto() {
    this.bProcessing = true
    await this.loadPhotoFileFromServer()
    this.bProcessing = false
  }


  async onRemovePhotoFile() {
    const deleteFn = async () => {
      try {
        this.bProcessing = true
        this.bSaving = true
        this.errorMsg = ''
        await this.photoService.beginPhotoTransaction(this.photoDoc._id)
        await this.deletePhotoFile()
      } catch ( err ) {
        console.error(err)
        this.errorMsg =  this.scoutTaskService.extractErrorMsg(err)
      } finally {
        this.photoService.endPhotoTransaction(this.photoDoc._id)
        this.bProcessing = false
        this.bSaving = false
      }
    }
    let initialState = {
      onYes: () => {
        deleteFn()
      },
      question: `Remove photo image (cannot be undone)?`,
      focusOnNo: true,
      yesStyle: 'btn btn-danger'
    }
    this.bsModalService.show(ConfirmModalComponent, {initialState} );
  }


  async onRotatePhotoUrl( angle : number ) {
    this.bProcessing = true
    this.bAlteringPhoto = true
    this.errorMsg=''
    var img = new Image()
    img.onload = async () => {
      try {
        const dataUrl = this.rotate(img,angle)
        this.photoUrl = dataUrl
        this.photoFile = await this.photoService.dataUrlToFileObject(
          dataUrl,
          this.photoFile ? this.photoFile.name : this.photo.fileName,
          this.photoFile ? this.photoFile.type : this.photo.mimeType)
        this.photoForm.form.markAsDirty()
        this.editChange.emit(this.photoForm) // update save btn on parent reference dialog
      } catch ( error ) {
        console.error( error )
        this.errorMsg = "Error occured during rotate photo"
      } finally {
        this.bAlteringPhoto = this.bProcessing = false;
      }
    }
    img.onerror = (evt) => this.bAlteringPhoto = this.bProcessing = false
    img.src = this.photoUrl
  }


  async onSave(bReset:boolean=true) {
    console.log('onSave(bReset='+bReset+')')
    this.errorMsg = ""

    try {
      this.bProcessing = true
      this.bSaving = true

      if ( !this.photoDoc._id ) {
        console.log('Saving a new photo. photoFile.name=' + this.photoFile?.name)
        this.photoDoc._id = `${this.scoutTaskDoc._id}/${this.scoutPathId}/${this.scoutPoint.id}/${uuidv4()}`
        this.photoDoc.scoutPointId = this.scoutPoint.id
        this.photoService.assignWhoAndWhen(this.photoDoc)
        await this.photoDoc.save()
        this.photo._id = this.photoDoc._id
      }

      await this.photoService.beginPhotoTransaction(this.photoDoc._id)
      try{
      this.uploadProgressSubscription?.unsubscribe()
      this.downloadProgressSubscription?.unsubscribe()

      const bFileChanged = this.photoFile != null
      //console.log('bFileChanged='+ bFileChanged)

      if ( bFileChanged ) {
        this.photo.fileName = this.photoFile.name
        this.photo.mimeType = this.photoFile.type
        this.photo.uploadDate = null
        const photoUrlHash = Md5.hashStr(this.photoUrl) as string
        this.photo.fileChecksum = photoUrlHash
        await this.photoService.saveLocalPhotoFile( this.photo, this.photoUrl )
        this.photoFile = null
      } else if ( !this.photo.fileName ) {
        try {
          await this.photoService.removeLocalPhotoFile( this.photo._id )
        } catch ( error ) {
          console.error('Could not remove local photo file for photo doc with no image')
        }
      }

      await this.updatePhotoMetaData()
      this.photoForm.form.markAsPristine() // may have forced marking form as dirty if just a image upload
      this.bProcessing = false
      this.bSaving = false
      if ( bReset ) {
        this.reset()
      }
      this.savedEmitter.emit(this.photoDoc)
      } finally {
        this.photoService.endPhotoTransaction(this.photoDoc._id)
      }
    } catch ( err ) {
      this.errorMsg = this.scoutTaskService.extractErrorMsg(err)
      this.bProcessing = false
      this.bSaving = false
      throw err
    } finally {
    }
  }

  bShowWebCamPending = false // way to keep muted background when re-taking a photo

  onShowWebcam(bClearPhotoUrl:boolean=false) {
    this.bShowWebcamView=false
    this.bShowWebCamPending=true
    if ( this.maximizeCamView ) {
      this.onResize()
    } else {
      this.webcamViewWidth = this.outerDivRef.nativeElement.getBoundingClientRect().width
      this.webcamViewHeight = this.webcamViewWidth * 9.0 / 16.0
    }
    if ( !this.selectedCameraId ) {
      const camera = this.photoService.getDefaultCamera()
      this.selectedCameraId = camera?.deviceId
    }
    this.updateWebcamVideoOptions(this.selectedCameraId)
    console.log('webcamViewWidth='+this.webcamViewWidth)
    timer(0).subscribe(ms=>{
      if ( bClearPhotoUrl ) {
        this.photoUrl = null
      }
      this.bShowWebcamView=true
      this.bShowWebCamPending=false
    })
  }

  autoAcceptNewPhotoSub : Subscription
  bInspectNewPhoto = false
  autoAcceptNewPhotoCountdown : number
  async onWebcamImageCreated(webcamImage: WebcamImage) {
    console.log('onWebcamImageCreated()', webcamImage);
    this.errorMsg = ""
    this.bProcessing = true
    try {
      this.photoForm.form.markAsDirty()
      this.editChange.emit(this.photoForm) // signal parent reference dialog to go into edit mode (form marked as dirty)
      this.photo.fileName = 'Snapshot_' + this.photoService.nowTimeStamp() + '.' + this.webcamImageType.substr(this.webcamImageType.lastIndexOf('/')+1);
      this.photo.mimeType = this.webcamImageType
      this.photoUrl = webcamImage.imageAsDataUrl
      this.photoFile = await this.photoService.dataUrlToFileObject(this.photoUrl,this.photo.fileName,this.photo.mimeType)
      if ( this.maximizeCamView ) {
        this.bInspectNewPhoto = true
        this.autoAcceptNewPhotoCountdown = 10
        this.autoAcceptNewPhotoSub = timer(0,1000).pipe(takeWhile(_=>this.bInspectNewPhoto)).subscribe(_=>{
          this.autoAcceptNewPhotoCountdown--
          if ( this.autoAcceptNewPhotoCountdown <= 0 ) {
              this.bInspectNewPhoto=false
          }
        })
      }
    } catch ( error ) {
      this.errorMsg = this.photoService.extractErrorMsg(error)
    }
    this.bProcessing = false
    this.bShowWebcamView = false
  }


  public onWebcamInitError(error: WebcamInitError) : void {
    console.error(error)
    this.errorMsg = error.message
    this.errorMsg += ".  Resolution may not be supported or another application has the camera open."
  }


  public onWebcamSwitched( deviceId : string ) {
    console.log('onWebcamSwitched() ' + deviceId)
  }

  bRefreshingCameraList = false
  async refreshCameraList(evt: any) {
    evt.stopPropagation();
    this.bRefreshingCameraList = true
    try {
      await this.photoService.updateCompatibleCameras()
    } catch ( error ) {
      console.error(error)
    }
    this.bRefreshingCameraList = false
  }

  reset() {
    //console.log('reset() ' + this.photoDoc?.fileName)
    this.errorMsg = ''
    this.uploadProgressSubscription?.unsubscribe()
    this.downloadProgressSubscription?.unsubscribe()
    this.photo = this.photoDoc?.toJSON()
    this.resetFile()
    this.photoForm?.form?.markAsPristine()
  }


  resetFile() {
    //console.log('resetFile()')
    this.errorMsg = ""
    this.photoFile = null
    this.fileName = '';
    if ( this.photoUrl && this.photoDoc && this.photoDoc.fileChecksum == (Md5.hashStr(this.photoUrl) as string) ) {
      // same photo so don't reload
    } else {
      this.photoUrl = null
      if ( this.photoDoc?.fileName ) {
        this.loadPhotoFile()
      }
    }
  }


  rotate = ( img, angle: number) => {
    // only supports 90 deg increments
    let nativeElement = this.alterPhotoCanvas.nativeElement as HTMLCanvasElement
    nativeElement.width = angle%180 ? img.height : img.width;
    nativeElement.height = angle%180 ? img.width : img.height;
    var newCtx = nativeElement.getContext('2d') as CanvasRenderingContext2D;
    newCtx.save() ;
    newCtx.translate( nativeElement.width/2, nativeElement.height/2);
    newCtx.rotate(angle * Math.PI/180);
    newCtx.drawImage( img, -img.width/2, -img.height/2);
    newCtx.restore();
    return nativeElement.toDataURL()
  }


  async saveEdits(bReload:boolean) {
    //console.log('saveEdits()')
    try {
      if ( this.photoForm.form.dirty ) {
        await this.onSave(bReload)
      }
    } catch ( error ) {
      console.error('Failed saving edits for photo ' + (this.photo?.title?this.photo.title:this.photo?._id))
      throw error
    }
  }

  selectedCameraId:string
  setDefaultCamera( camera : MediaDeviceInfo ) {
    this.selectedCameraId = camera.deviceId
    this.appState.settings.setDefaultWebcamId(this.selectedCameraId)
    this.updateWebcamVideoOptions(camera.deviceId)
  }


  async updateWebcamVideoOptions( deviceId : string ) {
    let options : MediaTrackConstraints = {
      facingMode : {
        ideal: 'environment'
      },
      deviceId: deviceId
    }
    const cam = this.photoService.compatibleCameras.find(d=>d.deviceId==deviceId)
    if ( cam ) {
      const widths = (cam as any).widths
      if ( widths.length ) {
        options.width = widths[widths.length-1]
      }
      console.log('selected camera: ' + cam.label)
    }
    console.log(JSON.stringify(options))
    this.webcamVideoOptions = options
  }


  async updatePhotoMetaData() {
    //console.log('updatePhotoMetaData() photo._id=' + this.photo._id)
    const populateFn = (destPhoto : ScoutPointPhoto ) => {
      destPhoto.scoutPointId = this.scoutPoint.id
      destPhoto.modifiedDate = this.scoutTaskService.nowTimeStamp()
      destPhoto.userId = this.appState.getUser()._id
      destPhoto.uploadDate = this.photo.uploadDate
      destPhoto.fileName = this.photo.fileName
      destPhoto.fileChecksum = this.photo.fileChecksum
      destPhoto.mimeType = this.photo.mimeType
      destPhoto.title = this.photo.title
      destPhoto.notes = this.photo.notes === undefined ? null : this.photo.notes
      destPhoto.uploadDate = this.photo.uploadDate
      destPhoto.group = this.groupName
      destPhoto.groupOrderIndex = this.photo.groupOrderIndex
      destPhoto.pointType = this.scoutPoint.type
      return destPhoto
    }
    await this.photoDoc.atomicUpdate( populateFn );
    this.photoService.syncNow()
    //console.log('updatePhotoMetaData() photo result: ' + JSON.stringify(this.photoDoc.toJSON()))
  }

}
